import { ACHIEVEMENTS, CATALOG, rankFor, type SlotId } from "../game/catalog";
import type { GameStore } from "../game/store";

/**
 * Screen templates — recreated from the Claude Design handoff
 * (`Echo Dodge.dc.html`), with the Daily Seed and Timeline Rating additions.
 * All interactivity flows through data-action attributes handled in app.ts.
 */

export interface ResultsView {
  win: boolean;
  score: number;
  shardPts: number;
  bonus: number;
  combo: number;
  time: number;
  attr: string | null;
  isBest: boolean;
  training: boolean;
  daily: boolean;
  print: string;
  rankUp: string | null;
  unlockCards: { name: string; desc: string }[];
  saved: boolean;
  archiveFull: boolean;
  grid: string | null;
  copied: boolean;
}

const esc = (s: string): string =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export function homeScreen(store: GameStore, dailyPlayed: boolean, dailyBest: number): string {
  const best = store.data.bestClassic;
  const rank = rankFor(best);
  const bestStr = best > 0 ? best.toLocaleString() : "—";
  return `
  <div class="screen safe-top safe-bot" style="padding-left:26px;padding-right:26px" data-screen="home">
    <div style="text-align:center" class="fade-up">
      <div class="display" style="font-weight:900;font-size:40px;line-height:1.02;letter-spacing:2px;color:#F2F5FF;text-shadow:-3px 2px 0 rgba(255,97,210,.35),3px -2px 0 rgba(107,141,255,.35),0 0 40px rgba(66,218,246,.3)">ECHO<br>DODGE</div>
      <div style="margin-top:14px;font-size:13px;letter-spacing:.5px;color:#A7ABC0">Your safest path becomes the danger.</div>
    </div>
    <div style="flex:1"></div>
    <div style="display:flex;flex-direction:column;align-items:center;gap:14px" class="fade-up d1">
      <div style="display:flex;align-items:baseline;gap:8px" class="mono">
        <span style="font-size:10px;letter-spacing:3px;color:#A7ABC0">BEST</span>
        <span style="font-size:22px;font-weight:600;color:#FFE37B;text-shadow:0 0 18px rgba(255,227,123,.35)">${bestStr}</span>
      </div>
      <div class="mono" style="display:flex;align-items:center;gap:7px;font-size:9.5px;letter-spacing:2.5px;color:${rank.col}">
        <span style="width:6px;height:6px;border-radius:3px;background:${rank.col};box-shadow:0 0 8px ${rank.col};display:inline-block"></span>
        TIMELINE RATING · ${rank.name}
      </div>
      <div class="btn-primary" data-action="play" style="width:100%;height:66px;font-size:17px;letter-spacing:8px;text-indent:8px;animation:edGlow 2.6s ease-in-out infinite">PLAY</div>
    </div>
    <div class="fade-up d2" data-action="daily" style="margin-top:12px;height:52px;border-radius:16px;background:rgba(255,227,123,.06);border:1px solid rgba(255,227,123,.28);display:flex;align-items:center;justify-content:center;gap:10px;cursor:pointer">
      <span style="width:10px;height:10px;background:#FFE37B;transform:rotate(45deg);box-shadow:0 0 10px rgba(255,227,123,.8)"></span>
      <span class="display" style="font-weight:700;font-size:10.5px;letter-spacing:2.5px;color:#FFE37B">DAILY SEED</span>
      <span class="mono" style="font-size:9px;letter-spacing:1px;color:#A7ABC0">${dailyPlayed ? `TODAY ${dailyBest.toLocaleString()}` : "ONE ARENA · EVERYONE"}</span>
    </div>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:12px" class="fade-up d2">
      <div class="grid-btn" data-action="practice">PRACTICE</div>
      <div class="grid-btn" data-action="locker">LOCKER</div>
      <div class="grid-btn" data-action="prints">TRACEPRINTS</div>
      <div class="grid-btn" data-action="settings">SETTINGS</div>
    </div>
    <div data-action="getApp" class="fade-up d3" style="margin-top:14px;height:52px;border-radius:16px;background:rgba(66,218,246,.10);border:1px solid rgba(66,218,246,.45);display:flex;flex-direction:column;align-items:center;justify-content:center;cursor:pointer;gap:3px">
      <span class="display" style="font-weight:700;font-size:10.5px;letter-spacing:2.5px;color:#42DAF6">GET THE FULL GAME ON iPHONE · FREE</span>
      <span class="mono" style="font-size:8.5px;letter-spacing:1px;color:#A7ABC0">WEB DEMO · 7 PLANETS, POWER-UPS &amp; LEADERBOARDS ARE IN THE APP</span>
    </div>
  </div>`;
}

export function runHUDOverlay(): string {
  return `
  <div style="position:absolute;top:calc(env(safe-area-inset-top,0px) + 24px);right:14px;z-index:6">
    <div data-action="pause" style="width:44px;height:44px;border-radius:22px;background:rgba(18,28,50,.55);border:1px solid rgba(167,171,192,.22);display:flex;align-items:center;justify-content:center;cursor:pointer;backdrop-filter:blur(8px)">
      <div style="display:flex;gap:4px"><div style="width:4px;height:14px;border-radius:2px;background:#F2F5FF"></div><div style="width:4px;height:14px;border-radius:2px;background:#F2F5FF"></div></div>
    </div>
  </div>`;
}

export function tutorialOverlay(copy: string): string {
  return `
  <div style="position:absolute;inset:0;pointer-events:none;z-index:6">
    <div data-action="skipTut" style="position:absolute;top:calc(env(safe-area-inset-top,0px) + 32px);right:18px;pointer-events:auto;font:600 11px 'IBM Plex Mono',monospace;letter-spacing:2px;color:#A7ABC0;padding:12px 14px;cursor:pointer">SKIP</div>
    <div style="position:absolute;left:0;right:0;top:calc(env(safe-area-inset-top,0px) + 80px);text-align:center">
      <div class="display" style="display:inline-block;font-weight:700;font-size:15px;letter-spacing:4px;color:#F2F5FF;text-shadow:0 0 22px rgba(66,218,246,.55);animation:edFadeUp .5s ease both">${esc(copy)}</div>
    </div>
  </div>`;
}

export function goalCard(): string {
  return `
  <div style="position:absolute;inset:0;background:rgba(4,7,15,.66);backdrop-filter:blur(10px);display:flex;align-items:center;justify-content:center;z-index:8">
    <div style="width:290px;background:linear-gradient(180deg,#0E1A30,#0B1226);border:1px solid rgba(66,218,246,.25);border-radius:24px;padding:30px 26px;text-align:center;animation:edFadeUp .45s ease both;box-shadow:0 30px 80px rgba(0,0,0,.6)">
      <div style="display:flex;flex-direction:column;gap:18px;margin-bottom:26px">
        <div style="display:flex;align-items:center;gap:14px"><div style="width:14px;height:14px;background:#FFE37B;transform:rotate(45deg);box-shadow:0 0 14px rgba(255,227,123,.8);flex:none"></div><div class="display" style="font-size:12px;font-weight:700;letter-spacing:3px">COLLECT GOLD</div></div>
        <div style="display:flex;align-items:center;gap:14px"><div style="width:14px;height:14px;border-radius:50%;border:2.5px solid #FF61D2;box-shadow:0 0 14px rgba(255,97,210,.7);flex:none;box-sizing:border-box"></div><div class="display" style="font-size:12px;font-weight:700;letter-spacing:3px">AVOID YOUR ECHOES</div></div>
        <div style="display:flex;align-items:center;gap:14px"><div style="width:14px;height:14px;border-radius:50%;border:2.5px solid #42DAF6;border-top-color:transparent;flex:none;box-sizing:border-box"></div><div class="display" style="font-size:12px;font-weight:700;letter-spacing:3px">SURVIVE 30 SECONDS</div></div>
      </div>
      <div class="btn-primary" data-action="startTraining" style="height:56px;border-radius:28px;font-weight:700;font-size:12px;letter-spacing:3px;box-shadow:0 0 30px rgba(66,218,246,.4)">START TRAINING RUN</div>
    </div>
  </div>`;
}

export function resultsScreen(r: ResultsView): string {
  const headColor = r.win ? "#B9F8FF" : "#FF719A";
  const headShadow = r.win ? "rgba(66,218,246,.5)" : "rgba(255,113,154,.4)";
  const unlocks = r.unlockCards
    .map(
      (u) => `
      <div style="display:flex;align-items:center;gap:12px;background:rgba(255,227,123,.08);border:1px solid rgba(255,227,123,.3);border-radius:14px;padding:10px 14px;margin-bottom:8px" class="fade-up d2">
        <div style="width:10px;height:10px;background:#FFE37B;transform:rotate(45deg);box-shadow:0 0 10px rgba(255,227,123,.8);flex:none"></div>
        <div><div class="display" style="font-weight:700;font-size:10px;letter-spacing:2px;color:#FFE37B">UNLOCKED · ${esc(u.name)}</div><div style="font-size:11px;color:#A7ABC0;margin-top:2px">${esc(u.desc)}</div></div>
      </div>`,
    )
    .join("");
  return `
  <div class="screen safe-top safe-bot" data-screen="results" style="background:rgba(4,7,15,.58);backdrop-filter:blur(9px);padding-left:24px;padding-right:24px;z-index:7;overflow:hidden">
    <div style="text-align:center" class="fade-up">
      <div class="display" style="font-weight:700;font-size:17px;letter-spacing:4px;color:${headColor};text-shadow:0 0 26px ${headShadow}">${r.win ? "TIMELINE CLEARED" : "THE ECHOES CAUGHT YOU"}</div>
      ${r.attr ? `<div class="mono" style="margin-top:8px;font-weight:500;font-size:11.5px;color:#FF719A;letter-spacing:1px">${esc(r.attr)}</div>` : ""}
      ${r.training ? `<div class="mono" style="margin-top:8px;display:inline-block;font-weight:600;font-size:9px;letter-spacing:2px;color:#FFE37B;border:1px solid rgba(255,227,123,.4);border-radius:10px;padding:3px 10px">TRAINING RUN · UNRANKED</div>` : ""}
      ${r.daily ? `<div class="mono" style="margin-top:8px;display:inline-block;font-weight:600;font-size:9px;letter-spacing:2px;color:#42DAF6;border:1px solid rgba(66,218,246,.4);border-radius:10px;padding:3px 10px">DAILY SEED</div>` : ""}
    </div>
    <div style="text-align:center;margin-top:14px" class="fade-up d1">
      <div class="mono" style="font-weight:600;font-size:54px;line-height:1;color:#F2F5FF;text-shadow:0 0 34px rgba(66,218,246,.3)">${r.score.toLocaleString()}</div>
      ${r.isBest ? `<div class="display" style="margin-top:8px;display:inline-block;font-weight:700;font-size:10px;letter-spacing:3px;color:#04121C;background:linear-gradient(180deg,#FFF0B8,#FFE37B);border-radius:12px;padding:5px 14px;box-shadow:0 0 24px rgba(255,227,123,.45)">NEW BEST</div>` : ""}
      ${r.rankUp ? `<div class="mono" style="margin-top:8px;font-size:10px;letter-spacing:2.5px;color:#B9F8FF">RATING UP · ${esc(r.rankUp)}</div>` : ""}
    </div>
    <div class="mono fade-up d1" style="display:flex;gap:22px;justify-content:center;margin-top:12px">
      <div style="text-align:center"><div style="font-size:16px;font-weight:600;color:#FFE37B">${r.shardPts}</div><div style="font-size:8.5px;letter-spacing:2px;color:#A7ABC0;margin-top:2px">SHARDS</div></div>
      <div style="text-align:center"><div style="font-size:16px;font-weight:600;color:#B9F8FF">+${r.bonus}</div><div style="font-size:8.5px;letter-spacing:2px;color:#A7ABC0;margin-top:2px">SURVIVAL</div></div>
      <div style="text-align:center"><div style="font-size:16px;font-weight:600;color:#F2F5FF">x${r.combo}</div><div style="font-size:8.5px;letter-spacing:2px;color:#A7ABC0;margin-top:2px">COMBO</div></div>
      <div style="text-align:center"><div style="font-size:16px;font-weight:600;color:#F2F5FF">${r.time.toFixed(1)}s</div><div style="font-size:8.5px;letter-spacing:2px;color:#A7ABC0;margin-top:2px">TIME</div></div>
    </div>
    <div style="flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;min-height:0;margin:10px 0" class="fade-up d2">
      <div style="width:min(190px,26dvh);aspect-ratio:1;border-radius:14px;border:1px solid rgba(66,218,246,.3);box-shadow:0 16px 50px rgba(0,0,0,.55);background:url(${r.print}) center/cover"></div>
      <div class="mono" style="text-align:center;font-weight:500;font-size:8.5px;letter-spacing:2px;color:#A7ABC0">TRACEPRINT</div>
    </div>
    ${unlocks}
    ${r.grid ? `<div class="btn-secondary fade-up d2" data-action="copyGrid" style="height:40px;margin-bottom:10px;font-size:10px;letter-spacing:2px;${r.copied ? "color:#42DAF6" : ""}">${r.copied ? "COPIED ✓" : "COPY SEED GRID"}&nbsp;&nbsp;<span style="letter-spacing:0;font-size:13px">${r.grid}</span></div>` : ""}
    <div class="btn-primary fade-up d2" data-action="again" style="height:60px;border-radius:30px;font-size:15px;letter-spacing:6px;text-indent:6px;box-shadow:0 0 34px rgba(66,218,246,.45)">AGAIN</div>
    <div style="display:flex;gap:10px;margin-top:10px" class="fade-up d3">
      <div class="btn-secondary" data-action="shareTrace" style="flex:1;height:46px;color:#B9F8FF;border-color:rgba(66,218,246,.35)">SHARE</div>
      <div class="btn-secondary" data-action="saveTrace" style="flex:1;height:46px;${r.saved ? "color:#42DAF6" : ""}">${r.saved ? "SAVED ✓" : r.archiveFull ? "ARCHIVE FULL" : "SAVE"}</div>
      <div class="btn-secondary" data-action="homeBtn" style="flex:1;height:46px">HOME</div>
    </div>
  </div>`;
}

export function pauseOverlay(resuming: boolean, resumeNum: number): string {
  return `
  <div style="position:absolute;inset:0;background:rgba(4,7,15,.7);backdrop-filter:blur(10px);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:14px;z-index:9">
    ${
      resuming
        ? `<div class="display" style="font-weight:900;font-size:84px;color:#B9F8FF;text-shadow:0 0 50px rgba(66,218,246,.6);animation:edPulse .5s ease">${resumeNum}</div>`
        : `<div class="display" style="font-weight:700;font-size:20px;letter-spacing:6px;margin-bottom:14px">PAUSED</div>
    <div class="btn-primary" data-action="resume" style="width:250px;height:56px;border-radius:28px;font-size:13px;letter-spacing:4px">RESUME</div>
    <div class="btn-secondary" data-action="restart" style="width:250px;height:50px;border-radius:25px;font-size:11px;letter-spacing:3px">RESTART</div>
    <div class="btn-secondary" data-action="quit" style="width:250px;height:50px;border-radius:25px;font-size:11px;letter-spacing:3px">HOME</div>`
    }
  </div>`;
}

export function lockerScreen(store: GameStore, tab: SlotId, previewNote: string): string {
  const tabs: [SlotId, string][] = [
    ["core", "CORE"],
    ["trail", "TRAIL"],
    ["palette", "ECHOES"],
    ["shapes", "SHAPES"],
    ["shard", "SHARD"],
    ["arena", "ARENA"],
    ["instrument", "SOUND"],
    ["frame", "FRAME"],
  ];
  const tabHtml = tabs
    .map(([id, name]) => {
      const on = tab === id;
      return `<div data-action="lockerTab" data-tab="${id}" style="padding:9px 13px;border-radius:14px;flex:none;cursor:pointer;font:700 9px 'Unbounded',sans-serif;letter-spacing:1.5px;background:${on ? "rgba(66,218,246,.16)" : "rgba(18,28,50,.5)"};border:1px solid ${on ? "rgba(66,218,246,.45)" : "rgba(167,171,192,.14)"};color:${on ? "#42DAF6" : "#A7ABC0"}">${name}</div>`;
    })
    .join("");

  const items = CATALOG[tab]
    .map((it) => {
      const owned = store.owns(tab, it.id);
      const equipped = store.data.equipped[tab] === it.id;
      let sub: string;
      let subColor = "#5B6078";
      if (equipped) {
        sub = "EQUIPPED";
        subColor = "#42DAF6";
      } else if (owned) {
        sub = "TAP TO EQUIP";
      } else if (it.prism) {
        sub = "🔒 PRISM PACK";
        subColor = "#9F79FF";
      } else {
        sub = ACHIEVEMENTS[it.ach ?? ""]?.desc ?? "";
        subColor = "#A7ABC0";
      }
      const swatches = it.cols
        .map(
          (c) =>
            `<div style="width:20px;height:20px;border-radius:10px;background:${c};box-shadow:0 0 10px ${c}66;flex:none"></div>`,
        )
        .join("");
      return `
      <div data-action="lockerItem" data-slot="${tab}" data-id="${it.id}" style="background:${equipped ? "rgba(66,218,246,.1)" : "rgba(18,28,50,.5)"};border:1px solid ${equipped ? "rgba(66,218,246,.55)" : "rgba(167,171,192,.14)"};border-radius:16px;padding:14px;cursor:pointer;opacity:${owned ? 1 : 0.75}">
        <div style="display:flex;gap:6px;align-items:center;height:26px">${swatches}</div>
        <div class="display" style="font-weight:700;font-size:10px;letter-spacing:1.5px;color:#F2F5FF;margin-top:8px">${esc(it.name)}</div>
        <div class="mono" style="font-size:9.5px;letter-spacing:.5px;color:${subColor};margin-top:4px">${esc(sub)}</div>
      </div>`;
    })
    .join("");

  const core = store.equippedItem("core").data as { ring?: string; core?: string };
  const pal = store.equippedItem("palette").cols;
  const previewDots =
    `<div style="width:26px;height:26px;border-radius:13px;background:radial-gradient(circle at 38% 32%, ${core.core}, ${core.ring});box-shadow:0 0 18px ${core.ring};flex:none"></div>` +
    pal
      .map(
        (c) =>
          `<div style="width:18px;height:18px;border-radius:9px;border:2px solid ${c};box-shadow:0 0 12px ${c}88;box-sizing:border-box;flex:none"></div>`,
      )
      .join("") +
    `<div style="width:16px;height:16px;background:#FFE37B;transform:rotate(45deg);box-shadow:0 0 14px rgba(255,227,123,.8);flex:none"></div>`;

  return `
  <div class="screen safe-top safe-bot menu-bg" data-screen="locker">
    <div class="page-head">
      <div class="back-btn" data-action="homeBtn">‹</div>
      <div class="page-title">LOCKER</div>
    </div>
    <div style="margin:0 20px;background:radial-gradient(220px 120px at 50% 40%,#0E1E36,#0A1022);border:1px solid rgba(66,218,246,.16);border-radius:18px;padding:16px;display:flex;align-items:center;justify-content:center;gap:16px;min-height:64px">${previewDots}</div>
    <div class="mono" style="text-align:center;font-weight:500;font-size:9.5px;letter-spacing:2px;color:#A7ABC0;padding:8px 20px 4px;min-height:14px">${esc(previewNote)}</div>
    <div class="scroll" style="display:flex;gap:6px;padding:8px 20px;overflow-x:auto;overflow-y:hidden;flex:none">${tabHtml}</div>
    <div class="scroll" style="flex:1;padding:6px 20px 10px;display:grid;grid-template-columns:1fr 1fr;gap:10px;align-content:start">${items}</div>
  </div>`;
}

export function printsScreen(store: GameStore): string {
  const prints = store.data.prints;
  const empty = `
    <div style="flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;padding:0 40px;text-align:center">
      <div style="width:64px;height:64px;border-radius:50%;border:1.5px dashed rgba(167,171,192,.4)"></div>
      <div style="font-size:13px;color:#A7ABC0;line-height:1.5">Every run draws a route.<br>Clear one to mint your first Traceprint.</div>
    </div>`;
  const grid = `
    <div class="scroll" style="flex:1;padding:0 20px;display:grid;grid-template-columns:1fr 1fr;gap:12px;align-content:start">
      ${prints
        .map(
          (p) => `
        <div data-action="openPrint" data-id="${p.id}" style="cursor:pointer;position:relative">
          <div style="width:100%;aspect-ratio:1;border-radius:14px;border:1px solid rgba(66,218,246,.22);box-sizing:border-box;background:url(${p.url}) center/cover"></div>
          ${p.fav ? `<div style="position:absolute;top:8px;right:8px;color:#FFE37B;font-size:13px;text-shadow:0 0 8px rgba(0,0,0,.8)">★</div>` : ""}
          <div class="mono" style="font-weight:500;font-size:9px;letter-spacing:1px;color:#A7ABC0;margin-top:6px">${p.score.toLocaleString()} PTS · ${p.dateISO.slice(0, 10)}</div>
        </div>`,
        )
        .join("")}
    </div>`;
  return `
  <div class="screen safe-top safe-bot menu-bg" data-screen="prints">
    <div class="page-head">
      <div class="back-btn" data-action="homeBtn">‹</div>
      <div class="page-title">TRACEPRINTS</div>
      <div class="mono" style="margin-left:auto;font-size:9px;letter-spacing:1px;color:#5B6078">${prints.length}/100</div>
    </div>
    ${prints.length === 0 ? empty : grid}
  </div>`;
}

export function printDetail(store: GameStore, id: string): string {
  const p = store.data.prints.find((x) => x.id === id);
  if (!p) return "";
  return `
  <div style="position:absolute;inset:0;background:rgba(4,7,15,.85);backdrop-filter:blur(12px);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:18px;z-index:10;padding:0 28px">
    <div style="width:100%;max-width:320px;aspect-ratio:1;border-radius:18px;border:1px solid rgba(66,218,246,.3);box-shadow:0 30px 90px rgba(0,0,0,.7);background:url(${p.url}) center/cover"></div>
    <div class="mono" style="font-weight:500;font-size:11px;letter-spacing:2px;color:#A7ABC0">${p.score.toLocaleString()} PTS · x${p.combo} · ${p.dateISO.slice(0, 10)}${p.kind === "daily" ? " · DAILY" : ""}</div>
    <div style="display:flex;gap:10px;width:100%;max-width:320px">
      <div class="btn-secondary" data-action="favPrint" data-id="${p.id}" style="flex:1;height:48px;font-family:'Space Grotesk',sans-serif;font-size:12px;letter-spacing:0;color:#FFE37B">${p.fav ? "★ FAVORITED" : "☆ FAVORITE"}</div>
      <div class="btn-secondary" data-action="sharePrint" data-id="${p.id}" style="flex:1;height:48px">SHARE</div>
      <div data-action="deletePrint" data-id="${p.id}" style="flex:1;height:48px;border-radius:24px;background:rgba(255,113,154,.1);border:1px solid rgba(255,113,154,.35);display:flex;align-items:center;justify-content:center;font:700 9.5px 'Unbounded',sans-serif;letter-spacing:2px;color:#FF719A;cursor:pointer">DELETE</div>
    </div>
    <div class="display" data-action="closePrint" style="margin-top:6px;font-weight:700;font-size:10px;letter-spacing:3px;color:#A7ABC0;cursor:pointer;padding:12px">CLOSE</div>
  </div>`;
}

export function shopScreen(store: GameStore, price: string): string {
  const owned = store.data.prism;
  return `
  <div class="screen safe-top safe-bot menu-bg" data-screen="shop">
    <div class="page-head">
      <div class="back-btn" data-action="homeBtn">‹</div>
      <div class="page-title">SHOP</div>
    </div>
    <div class="scroll" style="flex:1;padding:0 20px">
      <div style="background:linear-gradient(135deg,rgba(255,97,210,.14),rgba(107,141,255,.14) 50%,rgba(66,218,246,.14));border:1px solid rgba(159,121,255,.35);border-radius:22px;padding:24px 22px">
        <div style="display:flex;gap:8px;margin-bottom:16px">
          <div style="width:22px;height:22px;border-radius:50%;background:radial-gradient(circle at 35% 30%,#FFB4E8,#FF61D2);box-shadow:0 0 12px rgba(255,97,210,.6)"></div>
          <div style="width:22px;height:22px;border-radius:50%;background:radial-gradient(circle at 35% 30%,#CBB6FF,#9F79FF);box-shadow:0 0 12px rgba(159,121,255,.6)"></div>
          <div style="width:22px;height:22px;border-radius:50%;background:radial-gradient(circle at 35% 30%,#FFA9BC,#FF5478);box-shadow:0 0 12px rgba(255,84,120,.6)"></div>
          <div style="width:22px;height:22px;border-radius:50%;background:radial-gradient(circle at 35% 30%,#AFC3FF,#6B8DFF);box-shadow:0 0 12px rgba(107,141,255,.6)"></div>
        </div>
        <div class="display" style="font-weight:900;font-size:17px;letter-spacing:1px;line-height:1.25">PRISM TIMELINE<br>PACK</div>
        <div style="font-size:12.5px;color:#A7ABC0;margin-top:10px;line-height:1.55">Every item is visual only. No speed, no shields, no score advantage — ever.</div>
        <div class="mono" style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:18px;font-weight:500;font-size:10px;letter-spacing:1px;color:#DDE2F5">
          <div>· 6 echo palettes</div><div>· 4 player cores</div>
          <div>· 4 trails</div><div>· 3 arenas</div>
          <div>· 4 print frames</div><div>· 2 instruments</div>
        </div>
        ${
          owned
            ? `<div style="margin-top:20px;height:54px;border-radius:27px;border:1px solid rgba(66,218,246,.4);color:#42DAF6;display:flex;align-items:center;justify-content:center;font:900 12px 'Unbounded',sans-serif;letter-spacing:2px">OWNED ✓</div>`
            : `<div data-action="getApp" style="margin-top:20px;height:54px;border-radius:27px;background:linear-gradient(180deg,#F2F5FF,#C9D2F0);color:#0B1226;display:flex;align-items:center;justify-content:center;gap:10px;font:900 12px 'Unbounded',sans-serif;letter-spacing:2px;cursor:pointer">GET IT ON THE APP STORE · ${price}</div>`
        }
      </div>
      <div style="text-align:center;padding:20px;font-size:12px;color:#A7ABC0">Purchases live in the iPhone app. This browser version is a free demo.</div>
      <div style="text-align:center;font-size:10px;color:#5B6078;padding:0 20px 20px;line-height:1.5">One-time purchase. Cosmetics never change hitboxes, speed, or scoring.</div>
    </div>
  </div>`;
}

export function shopSheet(price: string): string {
  return `
  <div style="position:absolute;inset:0;background:rgba(0,0,0,.5);display:flex;align-items:flex-end;z-index:11">
    <div style="width:100%;background:#161B2E;border-radius:22px 22px 0 0;padding:24px 24px calc(env(safe-area-inset-bottom,0px) + 40px);box-sizing:border-box;animation:edFadeUp .3s ease both">
      <div style="display:flex;justify-content:space-between;align-items:center"><div style="font-family:-apple-system,system-ui;font-size:15px;font-weight:600">App Store</div><div data-action="cancelBuy" style="color:#8E8E93;font-size:14px;cursor:pointer;padding:6px">Cancel</div></div>
      <div style="display:flex;gap:14px;margin-top:18px;align-items:center">
        <div style="width:52px;height:52px;border-radius:12px;background:linear-gradient(135deg,#FF61D2,#6B8DFF);flex:none"></div>
        <div><div style="font-family:-apple-system,system-ui;font-size:14px;font-weight:600">Prism Timeline Pack</div><div style="font-family:-apple-system,system-ui;font-size:12px;color:#8E8E93;margin-top:2px">Echo Dodge · In-App Purchase</div></div>
        <div style="margin-left:auto;font-family:-apple-system,system-ui;font-size:14px;font-weight:600">${price}</div>
      </div>
      <div data-action="confirmBuy" style="margin-top:22px;height:50px;border-radius:14px;background:#0A84FF;display:flex;align-items:center;justify-content:center;font-family:-apple-system,system-ui;font-size:15px;font-weight:600;color:#fff;cursor:pointer">Confirm with Side Button</div>
    </div>
  </div>`;
}

export function settingsScreen(store: GameStore): string {
  const s = store.data.settings;
  const tog = (key: string, label: string, sub: string, on: boolean): string => `
    <div class="row">
      <div style="flex:1"><div class="row-label">${label}</div><div class="row-sub">${sub}</div></div>
      <div class="toggle ${on ? "on" : "off"}" data-action="toggleSetting" data-key="${key}"><div class="knob"></div></div>
    </div>`;
  const offsetOpts = ([[44, "HIGH"], [24, "MED"], [0, "NONE"]] as [number, string][])
    .map(
      ([v, label]) =>
        `<div data-action="setOffset" data-v="${v}" style="padding:8px 12px;border-radius:10px;cursor:pointer;font:600 9px 'IBM Plex Mono',monospace;letter-spacing:1px;background:${s.offset === v ? "rgba(66,218,246,.2)" : "transparent"};color:${s.offset === v ? "#42DAF6" : "#5B6078"}">${label}</div>`,
    )
    .join("");
  return `
  <div class="screen safe-top safe-bot menu-bg" data-screen="settings">
    <div class="page-head">
      <div class="back-btn" data-action="homeBtn">‹</div>
      <div class="page-title">SETTINGS</div>
    </div>
    <div class="scroll" style="flex:1;padding:0 20px">
      ${tog("music", "Music", "Ambient timeline loop", s.music)}
      ${tog("fx", "Sound effects", "Pickups, warnings, hits", s.fx)}
      ${tog("haptics", "Haptics", "Impact feedback", s.haptics)}
      ${tog("reduced", "Reduced Motion", "Removes glow, particles, drift", s.reduced)}
      ${tog("contrast", "High Contrast", "Patterned trails, brighter edges", s.contrast)}
      ${tog("tether", "Persistent touch tether", "Keep the target line visible", s.tether)}
      ${tog("nudge", "Screen nudge", "2-unit directional bump on hit", s.nudge)}
      <div class="row">
        <div style="flex:1"><div class="row-label">Finger offset</div><div class="row-sub">Keeps the runner above your finger</div></div>
        <div style="display:flex;gap:4px;background:rgba(18,28,50,.7);border-radius:12px;padding:3px">${offsetOpts}</div>
      </div>
      <div class="row" data-action="replayTut" style="cursor:pointer;color:#42DAF6;font-size:13.5px;font-weight:500">Replay tutorial</div>
      <div class="row" data-action="restore" style="cursor:pointer;font-size:13.5px;font-weight:500">Restore Purchases</div>
      <div class="row" data-action="privacy" style="cursor:pointer;font-size:13.5px;color:#A7ABC0">Privacy</div>
      <div class="row" data-action="support" style="cursor:pointer;font-size:13.5px;color:#A7ABC0">Support</div>
      <div class="row" style="font-size:13.5px;color:#A7ABC0;border-bottom:none">Credits — deterministic echoes, handcrafted light</div>
    </div>
  </div>`;
}

export function practiceScreen(
  cfg: { A: boolean; B: boolean; C: boolean; D: boolean; collision: boolean; infinite: boolean; grid: boolean },
  palette: string[],
): string {
  const defs = [
    ["A", "-2.4S"],
    ["B", "-4.6S"],
    ["C", "-1.35S"],
    ["D", "-6.2S"],
  ] as const;
  const echoRows = defs
    .map(([id, delay], i) => {
      const on = cfg[id];
      return `
      <div class="row">
        <div style="width:14px;height:14px;border-radius:7px;border:2.5px solid ${palette[i]};box-sizing:border-box;box-shadow:0 0 10px ${palette[i]}88;flex:none"></div>
        <div style="flex:1;margin-left:12px"><div class="row-label">Echo ${id}</div><div class="mono row-sub" style="font-weight:500;font-size:10px">DELAY ${delay}</div></div>
        <div class="toggle ${on ? "on" : "off"}" data-action="practiceEcho" data-id="${id}"><div class="knob"></div></div>
      </div>`;
    })
    .join("");
  const rule = (key: "collision" | "infinite" | "grid", label: string, sub: string): string => `
    <div class="row">
      <div style="flex:1"><div class="row-label">${label}</div><div class="row-sub">${sub}</div></div>
      <div class="toggle ${cfg[key] ? "on" : "off"}" data-action="practiceRule" data-key="${key}"><div class="knob"></div></div>
    </div>`;
  return `
  <div class="screen safe-top safe-bot menu-bg" data-screen="practice">
    <div class="page-head" style="padding-bottom:6px">
      <div class="back-btn" data-action="homeBtn">‹</div>
      <div class="page-title">PRACTICE LAB</div>
    </div>
    <div style="padding:0 20px 8px;font-size:11.5px;color:#5B6078">No score, no unlocks — a room to study your echoes.</div>
    <div class="scroll" style="flex:1;padding:0 20px">
      <div class="mono" style="font-weight:600;font-size:9.5px;letter-spacing:2px;color:#A7ABC0;padding:14px 0 6px">ECHOES</div>
      ${echoRows}
      <div class="mono" style="font-weight:600;font-size:9.5px;letter-spacing:2px;color:#A7ABC0;padding:16px 0 6px">RULES</div>
      ${rule("collision", "Collisions", "Off = ghost through your echoes")}
      ${rule("infinite", "Infinite time", "No 30-second timer")}
      ${rule("grid", "Route grid", "Persistent grid overlay")}
    </div>
    <div style="padding:14px 20px 0">
      <div class="btn-primary" data-action="startPractice" style="height:56px;border-radius:28px;font-weight:900;font-size:13px;letter-spacing:4px">ENTER LAB</div>
    </div>
  </div>`;
}
